import React, { useState, useEffect, useCallback, useMemo } from "react";

//import localization
import { useTranslation } from "react-i18next";

import countryCodes from "country-codes-list";

import DatePicker from "../utils/DatePicker";
import { Row, Col, Form, Select, Input, Divider, Checkbox, Button } from "antd";

import { DatePatterns, onLegalPapersClick } from "../utils/consts";

import { stateContext } from "./MainView";

const { Option } = Select;

//Initial country code prefixes (e.g: +30, +45...) turned into an array because the original structure is an object
const countryCodesObject = [
	...new Set(
		Object.values(countryCodes.customList("countryCode", "+{countryCallingCode}")).sort((first, second) => {
			if (first === second) return 0;
			if (second === "+30" || second === "+7") return 1;
			if (first === "+30" || first === "+7") return -1;

			if (first < second) return -1;
			if (first > second) return 1;
			return 0;
		})
	)
];

function TravellersForm({ metaValues, localeAntD, query }) {
	//Locale Hooks
	const { t } = useTranslation();

	//formState: from stateContext
	const [formState, setFormState] = React.useContext(stateContext);

	//form: Form Initialization
	const [form] = Form.useForm();

	const [countryState, setCountryState] = useState(formState?.TravellersForm?.Contractor?.Location?.Country);

	// Rule for text inputs that disallows the user to give non-latin characters
	const nonLatinRule = useMemo(() => {
		return { pattern: /^[a-zA-Z0-9@~`!@#$%^&*() _=+\\\\';:"\\/?>.<,-]+$/i, message: t("FillInWithLatinCharacters") };
	}, [t]);

	//Function that sets the TravellersForm from the Form
	const onFinish = (values) => {
		let { Travellers, ...contractor } = values;

		setFormState((formState) => {
			return {
				...formState,
				CurrentStep: formState.CurrentStep + 1,
				TravellersForm: {
					Travellers: values.Travellers,
					Contractor: contractor
				}
			};
		});
	};

	// Function resets ZipCode when country changes
	const onCountryChange = useCallback(
		(value) => {
			setCountryState(value);
			form.setFieldsValue({
				ZipCode: ""
			});
		},
		[form]
	);

	//Function that updates the fields for the FirstName,LastName of the contractor from the first traveller
	const onFirstTravellerInfoChange = useCallback(
		({ type, value }) => {
			if (type === "FirstName") {
				form.setFieldsValue({
					FirstName: value
				});
			} else if (type === "LastName") {
				form.setFieldsValue({
					LastName: value
				});
			} else if (type === "Gender")
				form.setFieldsValue({
					Gender: value
				});
		},
		[form]
	);

	//On init set scroll position to top
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	return (
		<>
			<Form
				style={{ padding: "24px 24px 2px 24px" }}
				form={form}
				name="travellersFo"
				layout="vertical"
				requiredMark={false}
				scrollToFirstError={true}
				initialValues={
					formState?.TravellersForm?.Contractor?.FirstName
						? {
								...formState?.TravellersForm?.Contractor,
								Travellers: formState?.TravellersForm?.Travellers,
								agreement: false
						  }
						: {
								Travellers: formState.ProductForm.Travellers.map((item) => {
									return {
										BirthDate: item.BirthDate,
										Money: item.Money
									};
								}),
								Birth: formState.ProductForm.Travellers[0].BirthDate,
								Certificate: {
									IdTypeId: JSON.stringify(metaValues.CertificationTypes[0]),
									IdNo: ""
								}
						  }
				}
				onFinish={onFinish}
			>
				<h1 style={{ textAlign: "center" }}>{t("Travellers")}</h1>
				<Form.List name="Travellers">
					{(fields) => (
						<>
							{fields.map(({ key, name, fieldKey, ...restField }) => {
								//key holds the current key created which is incremental
								//name holds the index of each form item
								//restField holds isListField object which is required for each Form item
								return (
									<div key={`traveller${key}`}>
										<Divider orientation="left">
											{name + 1}# {t("Traveller")}
										</Divider>
										<Row gutter={12} key={key}>
											<Col xs={24} sm={12} lg={12}>
												<Form.Item label={t("FirstName")}>
													<Input.Group compact>
														<Form.Item {...restField} name={[name, "Gender"]} fieldKey={[fieldKey, "Gender"]} noStyle rules={[{ required: true, message: t("MissingInformation") }]}>
															<Select
																placeholder={t("Gender")}
																style={{ width: "30%" }}
																dropdownStyle={{ minWidth: 90 }}
																onChange={(value) => {
																	if (name === 0) onFirstTravellerInfoChange({ type: "Gender", value: value });
																}}
															>
																<Option value={true}>{t("Mr")}</Option>
																<Option value={false}>{t("Mrs")}</Option>
															</Select>
														</Form.Item>
														<Form.Item {...restField} name={[name, "FirstName"]} fieldKey={[fieldKey, "FirstName"]} noStyle rules={[{ required: true, message: t("MissingInformation") }, nonLatinRule]}>
															<Input
																onChange={({ target }) => {
																	if (name === 0) onFirstTravellerInfoChange({ type: "FirstName", value: target.value });
																}}
																style={{ width: "70%" }}
															/>
														</Form.Item>
													</Input.Group>
												</Form.Item>
											</Col>
											<Col xs={24} sm={12} lg={12}>
												<Form.Item
													{...restField}
													label={t("LastName")}
													name={[name, "LastName"]}
													fieldKey={[fieldKey, "LastName"]}
													rules={[
														{
															required: true,
															message: t("MissingInformation")
														},
														nonLatinRule
													]}
												>
													<Input
														onChange={({ target }) => {
															if (name === 0) onFirstTravellerInfoChange({ type: "LastName", value: target.value });
														}}
													/>
												</Form.Item>
											</Col>

											<Col xs={24} sm={12} lg={12}>
												<Form.Item {...restField} label={t("BirthDate")} name={[name, "BirthDate"]} fieldKey={[fieldKey, "BirthDate"]}>
													<p>{formState.ProductForm.Travellers[name].BirthDate.format(DatePatterns.LongDate)}</p>
												</Form.Item>
											</Col>
											<Col span={0}>
												<Form.Item {...restField} label="Money" name={[name, "Money"]} fieldKey={[fieldKey, "Money"]}>
													<p>{formState.ProductForm.Travellers[name].Money}</p>
												</Form.Item>
											</Col>
										</Row>
									</div>
								);
							})}
						</>
					)}
				</Form.List>
				<h1 style={{ textAlign: "center" }}>{t("PolicyHolderDetails")}</h1>
				<Divider />
				<Row gutter={12}>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item label={t("FirstName")}>
							<Input.Group compact>
								<Form.Item name="Gender" noStyle rules={[{ required: true, message: t("MissingInformation") }]}>
									<Select placeholder={t("Gender")} style={{ width: "30%" }} dropdownStyle={{ minWidth: 90 }}>
										<Option value={true}>{t("Mr")}</Option>
										<Option value={false}>{t("Mrs")}</Option>
									</Select>
								</Form.Item>
								<Form.Item name="FirstName" noStyle rules={[{ required: true, message: t("MissingInformation") }, nonLatinRule]}>
									<Input style={{ width: "70%" }} />
								</Form.Item>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="LastName"
							label={t("LastName")}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								},
								nonLatinRule
							]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={12}>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item name="Birth" label={t("BirthDate")}>
							<DatePicker style={{ width: "100%" }} locale={localeAntD.DatePicker} format={DatePatterns.LongDate} />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item label={t("Certificate")}>
							<Input.Group compact>
								<Form.Item name={["Certificate", "IdTypeId"]} noStyle rules={[{ required: true, message: t("MissingInformation") }]}>
									<Select placeholder={t("Type")} style={{ width: "50%" }} dropdownStyle={{ minWidth: 180 }}>
										{metaValues.CertificationTypes.map((selection) => (
											<Option key={selection.ID} value={JSON.stringify(selection)}>
												{selection.Name}
											</Option>
										))}
									</Select>
								</Form.Item>
								<Form.Item name={["Certificate", "IdNo"]} noStyle rules={[{ required: true, message: t("MissingInformation") }, nonLatinRule]}>
									<Input placeholder={t("Number")} style={{ width: "50%" }} />
								</Form.Item>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item label={t("YourCountryOfResidence")}>
							<Input.Group compact>
								<Form.Item name={["Location", "Country"]} noStyle rules={[{ required: true, message: t("MissingInformation") }]}>
									<Select placeholder={t("Country")} style={{ width: "35%" }} dropdownStyle={{ minWidth: 150 }} showSearch={true} optionFilterProp="children" onChange={onCountryChange}>
										{metaValues.Countries.map((selection) => (
											<Option key={selection.ID} value={JSON.stringify(selection)}>
												{selection.Name}
											</Option>
										))}
									</Select>
								</Form.Item>
								<Form.Item name={["Location", "City"]} noStyle rules={[{ required: true, message: t("MissingInformation") }, nonLatinRule]}>
									<Input style={{ width: "65%" }} placeholder={t("City")} />
								</Form.Item>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="Street"
							label={t("Street")}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="ZipCode"
							label={t("PostalCode")}
							rules={[
								{
									required: countryState ? true : false,
									message: t("MissingInformation")
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										//Validator for when the user selects a country
										let locationValue = null;
										if (getFieldValue("Location")) locationValue = JSON.parse(getFieldValue("Location")?.Country);
										//12 === with ID of Greece
										if (locationValue?.ID === "12") {
											//Regex that accepts postal codes specifically for Greece (e.g 15478, 154 78)
											const regex = new RegExp("^\\d{3}\\s{0,1}\\d{2}$");

											if (regex.test(value)) return Promise.resolve();
											else return Promise.reject(new Error(t("InvalidPostalCode")));
										} else return Promise.resolve();
									}
								}),
								nonLatinRule
							]}
						>
							<Input disabled={!countryState} autoComplete="new-password" />
						</Form.Item>
					</Col>

					<Col xs={24} sm={12} lg={12}>
						<Form.Item label={t("MobileNumber")}>
							<Input.Group compact>
								<Form.Item name={["Mobile", "Prefix"]} noStyle rules={[{ required: true, message: t("MissingInformation") }]}>
									<Select
										style={{
											width: "30%"
										}}
										dropdownStyle={{ minWidth: 80 }}
										showSearch={true}
										placeholder={"+30,+90..."}
										optionFilterProp="children"
									>
										{countryCodesObject.map((item, index) => (
											<Option key={index} value={item}>
												{item}
											</Option>
										))}
									</Select>
								</Form.Item>
								<Form.Item
									name={["Mobile", "Number"]}
									noStyle
									rules={[
										{
											max: 10,
											pattern: "^[0-9]{10,10}",
											message: ""
										},
										{
											required: true,
											message: t("MissingInformation")
										}
									]}
								>
									<Input
										style={{
											width: "70%"
										}}
										placeholder={t("Number")}
									/>
								</Form.Item>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="Email"
							label={t("EmailAddress")}
							rules={[
								{
									type: "email",
									message: ""
								},
								{
									required: true,
									message: t("MissingInformation")
								},
								nonLatinRule
							]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="EmailVerification"
							label={t("ConfirmEmailAddress")}
							dependencies={["Email"]}
							hasFeedback
							rules={[
								{
									type: "email",
									message: ""
								},
								{
									required: true,
									message: t("MissingInformation")
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue("Email") === value) {
											return Promise.resolve();
										}

										return Promise.reject(new Error(t("PleaseConfirmYourEmailAddress")));
									}
								}),
								nonLatinRule
							]}
						>
							<Input
								autoComplete="new-password"
								onPaste={(e) => {
									e.preventDefault();
									return false;
								}}
								onCopy={(e) => {
									e.preventDefault();
									return false;
								}}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					name="Agreement"
					valuePropName="checked"
					rules={[
						{
							required: true,
							message: t("PleaseAcceptTheTermsConditions")
						}
					]}
				>
					<Checkbox>
						{t("IHaveReadAndAccept")}
						<div
							className="div-clickable"
							style={{ display: "inline" }}
							onClick={() => {
								onLegalPapersClick({ type: "TermsAndConditions", lng: query.Language });
							}}
						>
							{t("TermsAndConditionsPlural")}
						</div>
					</Checkbox>
				</Form.Item>

				<Form.Item
					style={{
						textAlign: "center"
					}}
				>
					<Button type="primary" block htmlType="submit">
						{t("Submit")}
					</Button>
				</Form.Item>
			</Form>
		</>
	);
}

export default React.memo(TravellersForm);
